import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldPlus, Trash2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { PERM_LABELS } from "@/i18n";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

export function ModRanks() {
  const { tr, lang } = useLang();
  const { apiErr } = useAuth();
  const [ranks, setRanks] = useState([]);
  const [perms, setPerms] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", permissions: [] });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  const load = () => {
    api.get("/ranks").then((r) => setRanks(r.data));
    api.get("/permissions").then((r) => setPerms(r.data));
  };
  useEffect(load, []);

  const togglePerm = (p) =>
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(p) ? f.permissions.filter((x) => x !== p) : [...f.permissions, p],
    }));

  const create = async (e) => {
    e.preventDefault();
    setError("");
    setCreating(true);
    try {
      await api.post("/ranks", form);
      toast.success(tr.mCreated2);
      setForm({ name: "", description: "", permissions: [] });
      load();
    } catch (err) {
      setError(apiErr(err?.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const del = async (id) => {
    await api.delete(`/ranks/${id}`);
    toast.success(tr.mDelete);
    load();
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
      <div className="space-y-3" data-testid="ranks-list">
        {ranks.length === 0 && <div className="rounded-lg border border-zinc-800/70 bg-[#111113] p-6 text-center text-[13px] text-zinc-600">{tr.mNoRanks}</div>}
        {ranks.map((r) => (
          <div key={r.id} data-testid={`rank-${r.id}`} className="rounded-lg border border-zinc-800/70 bg-[#111113] p-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[14px] font-semibold text-zinc-100">{r.name}</div>
                {r.description && <div className="text-[12px] text-zinc-500">{r.description}</div>}
              </div>
              <button onClick={() => del(r.id)} data-testid={`delete-rank-${r.id}`} className="text-zinc-500 hover:text-red-400">
                <Trash2 size={15} />
              </button>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {r.permissions.length ? r.permissions.map((p) => (
                <span key={p} className="rounded border border-blue-500/30 bg-blue-500/10 px-2 py-0.5 text-[11px] text-blue-300">
                  {PERM_LABELS[lang][p] || p}
                </span>
              )) : <span className="text-[12px] text-zinc-600">—</span>}
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={create} className="h-fit rounded-lg border border-zinc-800/70 bg-[#111113] p-5" data-testid="create-rank-form">
        <div className="mb-4 flex items-center gap-2 text-[13px] font-semibold text-zinc-100">
          <ShieldPlus size={16} className="text-blue-400" /> {tr.mCreateRank}
        </div>
        <div className="space-y-3">
          <div>
            <Label className="mb-1.5 block text-[12px] text-zinc-300">{tr.mRankName}</Label>
            <Input data-testid="rank-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="border-zinc-800 bg-zinc-950 text-[13px]" />
          </div>
          <div>
            <Label className="mb-1.5 block text-[12px] text-zinc-300">{tr.mRankDesc}</Label>
            <Input data-testid="rank-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="border-zinc-800 bg-zinc-950 text-[13px]" />
          </div>
          <div>
            <Label className="mb-2 block text-[12px] text-zinc-300">{tr.mPermissions}</Label>
            <div className="space-y-2">
              {perms.map((p) => (
                <label key={p} className="flex cursor-pointer items-start gap-2.5">
                  <Checkbox
                    data-testid={`perm-${p}`}
                    checked={form.permissions.includes(p)}
                    onCheckedChange={() => togglePerm(p)}
                    className="mt-0.5 border-zinc-700 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                  />
                  <span className="text-[12px] text-zinc-300">{PERM_LABELS[lang][p] || p}</span>
                </label>
              ))}
            </div>
          </div>
          {error && <div className="rounded-md border border-red-600/40 bg-red-600/10 px-3 py-2 text-[12px] text-red-300">{error}</div>}
          <Button data-testid="submit-rank" type="submit" disabled={creating} className="h-10 w-full bg-blue-600 text-[13px] text-white hover:bg-blue-700">
            {creating ? <Loader2 size={15} className="animate-spin" /> : <ShieldPlus size={15} />} {tr.mCreateRank}
          </Button>
        </div>
      </form>
    </div>
  );
}
